// Nomina y documentos de las plantas en alcance (PP-64), para tres cosas del
// Inicio: nombrar el vehiculo y el conductor de cada salida (PB-29: nunca un
// id tecnico), avisar los viajes asignados con un movil no elegible y los
// documentos que vencen esta semana. Una sola carga por planta para las tres,
// con la misma concurrencia y la misma logica que Vencimientos (PP-23).
//
// La nomina no se encuesta: cambia por escritura, y despues de asignar desde
// el Inicio se relee en silencio. Una planta que falla no tapa a las demas.
import { useCallback, useEffect, useRef, useState } from "react";
import type { PortalSession } from "../auth/session.js";
import { EXPIRIES_CONCURRENCY, type ExpiriesPort } from "../expiries/index.js";
import { FleetError } from "../fleet/port.js";
import { collectExpiries, mapWithConcurrency, type Expiries, type SiteExpiryInput } from "../logic/expiries.js";
import { accessKey, type SiteRoster } from "../logic/home.js";
import type { SiteAccess } from "../sites/port.js";

export interface LoadedRoster extends SiteRoster {
  /** Fecha operativa de la planta con la que la API calculo la elegibilidad. */
  today: string;
}

export type SiteFleetState =
  | { estado: "cargando" }
  | { estado: "denegado" }
  | { estado: "error" }
  | {
      estado: "listo";
      /** Por habilitacion (accessKey). */
      rosters: ReadonlyMap<string, LoadedRoster>;
      expiries: Expiries;
      failedSites: readonly string[];
    };

export interface SiteFleetHandle {
  state: SiteFleetState;
  /** Relee sin pasar por "cargando". */
  refresh: () => void;
}

export function useSiteFleet(port: ExpiriesPort, session: PortalSession, sites: readonly SiteAccess[]): SiteFleetHandle {
  const [state, setState] = useState<SiteFleetState>({ estado: "cargando" });
  const [round, setRound] = useState(0);
  const silentNext = useRef(false);
  // El shell pasa un arreglo nuevo en cada render: la clave evita releer.
  const sitesKey = JSON.stringify(sites.map((s) => ({ siteId: s.siteId, name: s.name, providerId: s.providerId })));

  useEffect(() => {
    let current = true;
    const silent = silentNext.current;
    silentNext.current = false;
    if (!silent) setState({ estado: "cargando" });
    const targets = JSON.parse(sitesKey) as SiteAccess[];
    void mapWithConcurrency(targets, EXPIRIES_CONCURRENCY, async (site): Promise<SiteExpiryInput> => {
      const fleetSite = { siteId: site.siteId, providerId: site.providerId };
      const [docs, vehicles, drivers] = await Promise.all([
        port.listDocuments(session, fleetSite),
        port.listVehicles(session, fleetSite),
        port.listDrivers(session, fleetSite),
      ]);
      return {
        site: { siteId: site.siteId, name: site.name, providerId: site.providerId },
        today: docs.today,
        documents: docs.documents,
        requiredDocuments: docs.requiredDocuments,
        vehicles: vehicles.items,
        drivers: drivers.items,
      };
    }).then((results) => {
      if (!current) return;
      const inputs: SiteExpiryInput[] = [];
      const failedSites: string[] = [];
      results.forEach((result, index) => {
        if (result.ok) inputs.push(result.value);
        else failedSites.push(targets[index]!.name);
      });
      if (targets.length > 0 && inputs.length === 0) {
        const denied = results.every((r) => !r.ok && r.error instanceof FleetError && r.error.kind === "access");
        setState({ estado: denied ? "denegado" : "error" });
        return;
      }
      const rosters = new Map<string, LoadedRoster>();
      for (const input of inputs) {
        rosters.set(accessKey(input.site), { today: input.today, vehicles: input.vehicles, drivers: input.drivers });
      }
      setState({ estado: "listo", rosters, expiries: collectExpiries(inputs), failedSites });
    });
    return () => {
      current = false;
    };
  }, [port, session, sitesKey, round]);

  const refresh = useCallback(() => {
    silentNext.current = true;
    setRound((n) => n + 1);
  }, []);

  return { state, refresh };
}
